// calculatePrice.js — VIENINTELĖ kainos/išklotinės skaičiavimo funkcija.
// Anksčiau čia buvo tik "Kainų suvestinės"/PDF eksporto logika, o mygtuko
// (viršuje) ir krepšelio kaina buvo skaičiuojama ATSKIRAI, savo logika,
// dialogs/PriceDialog.jsx viduje — abi versijos nesutapdavo ir kiekviena
// turėjo savų klaidų. Sujungta į vieną: dabar IR "Kainų suvestinė"/PDF, IR
// PriceDialog.jsx, IR krepšelio/viršutinio mygtuko kaina naudoja BŪTENT ŠITĄ
// funkciją, tad daugiau nebegali nesutapti.
//
// `grandTotal` — savikaina (medžiagos + furnitūra + darbai), BE projektavimo/
// administravimo antkainio. Tai rodo "Kainų suvestinė"/PDF eksportas.
// `finalPrice` — `grandTotal` + projektavimo/administravimo antkainis +
// admino rankiniu būdu pridėtos eilutės (adminDescriptionTotal, žr. "Paslaugos"
// žingsnį). Tai rodo viršutinis kainos mygtukas ir tai įrašoma į krepšelį.
// `assemblyPrice`/`carryPrice`/`deliveryPrice`/`measurementPrice` — PASIRENKAMOS
// paslaugos, sąmoningai NEĮSKAIČIUOTOS nei į grandTotal, nei į finalPrice.

import { groupCabinetsByFurniture } from '../utils/groupCabinetsByFurniture';
import { hingeCountForHeight } from '../utils/hardwareStandards';

// Plokštės kvadratūros apvalinimo logika pagal užduotį
// area<=0 → 0 (BE minimalaus apvalinimo) — anksčiau čia grąžindavo 3 net kai
// realaus ploto apskritai NĖRA (pvz. nė vienos MDF plokštės nepasirinkta),
// tad kainos suvestinėje atsirasdavo fantominė "MDF (karkasas): 3.00 m²"
// eilutė su realia >0 kaina, nors MDF apskritai nenaudojama (žr. ClickUp
// "MDF, LMDP (skirtingos plokštės)... priskaičiuoja, nors jie nėra pasirinkti").
const calculateBillableArea = (area) => {
    if (area <= 0) return 0;
    if (area < 2) return 3;
    if (area >= 2 && area < 3.5) return 4.5;
    if (area >= 3.5 && area < 5) return 6;
    if (area >= 5 && area < 6) return 7.5;
    if (area >= 6 && area < 12.5) return area * 1.25;
    return area * 1.2; // >= 12.5
};

// Kojelių kiekio skaičiavimas
const getLegCount = (widthMeters) => {
    const widthCm = widthMeters * 100;
    if (widthCm < 100) return 4;
    if (widthCm >= 100 && widthCm < 200) return 6;
    if (widthCm >= 200 && widthCm < 300) return 8;
    if (widthCm >= 300 && widthCm < 400) return 10;
    if (widthCm >= 400 && widthCm < 500) return 12;
    if (widthCm >= 500 && widthCm < 600) return 14;
    return 16;
};

// Išskleidžia sectionOptions reikšmę į tipų sąrašą (palaiko ir integer, ir array formatą —
// InnerSelectingDialog gali sukurti masyvą { type, height } įrašų vienoje sekcijoje).
const flattenSectionTypes = (val) => Array.isArray(val) ? val.map(i => i?.type) : [val];

// Falšjuosta/falšfasadas/papildomas šonas plotis metrais — ta pati formulė,
// kaip FurnitureGroup.jsx renderSideElements (tik ten skaičiuojama vaizdavimui,
// čia — kainai).
const sideElementWidthM = (el) => {
    if (!el || el.type === 0) return 0;
    if (el.type === 2 && el.width) return Number(el.width) / 100;
    return 0.018;
};

// Sekcijos ribų skaičiavimas iš cabinet.structure medžio — TIKSLI ta pati
// funkcija, kaip View2D.jsx/SingleCabinet3D.jsx naudoja braižymui (žr. jų
// getSectionBounds). Anksčiau durelių aukštis čia buvo skaičiuojamas
// apytiksliai kaip `cab.height / (cab.shelves.length + 1)` — tai teisinga tik
// kai durelės dengia LYGIAI vieną tarp lygiai išdėstytų lentynų susidariusį
// tarpą; realiai `mergedSections` durelės dažnai dengia KELIS `structure`
// medžio lapus nevienodo aukščio, todėl ta formulė duodavo iki 5x per mažą
// aukštį (pvz. 43cm vietoj 215cm pilno aukščio durelėms).
const getSectionBounds = (structure, path, innerWidth, innerHeight, wallThickness) => {
    const parts = String(path ?? '').split('-').slice(1);
    let node = structure || {};
    let x = 0, y = 0, w = innerWidth, h = innerHeight;
    for (const part of parts) {
        const i = parseInt(part, 10);
        if (!node.split || !node.children) break;
        const isH = node.split === 'h';
        const ratio = node.ratio ?? 0.5;
        const available = isH ? h - wallThickness : w - wallThickness;
        const child0Size = available * ratio;
        const child1Size = available * (1 - ratio);
        if (isH) {
            if (i === 0) { y += child1Size + wallThickness; h = child0Size; }
            else { h = child1Size; }
        } else {
            if (i === 1) { x += child0Size + wallThickness; w = child1Size; }
            else { w = child0Size; }
        }
        node = node.children[i];
    }
    return { x, y, width: w, height: h };
};

export const calculatePrice = (cabinets, settings) => {
    const safeCabinets = Array.isArray(cabinets) ? cabinets.filter(Boolean) : [];

    const {
        quality = 0,
        backWall,
        generalWidth = 3,
        generalHeight = 2.45,
        basisType = 0,
        mergedSections = [],
        doorPlates = {},
        doorHandles = {},
        doorMirrors = {},
        doorFillings = {},
        doorTypes = {},
        doorColors = {},
        sectionOptions = {},
        selectedPlate,
        leftPlate, topPlate, bottomPlate, rightPlate, separatePlates,
        skirtingCutout,
        fittingType,
        professionalServices,
        type: furnitureType,
        coverPlates = {},
        adminDescriptionItems: adminDescriptionItemsByFurnitureId = {},
    } = settings || {};

    // adminDescriptionItems saugomos PER furnitureId (vienas projektas gali turėti
    // kelis baldus — žr. Layout.jsx furnitureConfig komentarą). Čia sujungiame tik
    // tų baldų, kurių skydeliai realiai yra `cabinets` parametre, eilutes — taip ši
    // funkcija teisingai veikia ir skaičiuojant VIENĄ baldą (aggregateByFurniture),
    // ir VISĄ projektą iškart (PriceDialog/krepšelio kaina).
    const adminFurnitureIds = [...new Set(cabinets && Array.isArray(cabinets) ? cabinets.map(c => c?.furnitureId).filter(Boolean) : [])];
    const adminDescriptionItems = adminFurnitureIds.flatMap(fid => adminDescriptionItemsByFurnitureId?.[fid] || []);

    const wallThickness = 0.018;

    // `generalWidth`/`generalHeight` (settings) yra tik VIENĄ KARTĄ nustatomi
    // pradiniai (arba fallback numatytieji: 3m/2.45m) laukai, kurie NIEKADA
    // neatnaujinami vėliau keičiant baldo/sekcijų matmenis (Layout.jsx
    // setGeneralWidth/setGeneralHeight apskritai niekur nekviečiami) — tad
    // realiam projektui jie dažnai lieka reikšmės, neturinčios nieko bendro su
    // tikru baldo dydžiu. Realų, TEISINGĄ bendrą plotį/aukštį skaičiuojame
    // TIESIOGIAI iš `cabinets` (ta pati groupCabinetsByFurniture, kuri jau
    // naudojama žemiau falšjuostų/dengiančių plokščių skaičiavimui), o ne iš
    // šio nepatikimo settings lauko (žr. ClickUp "Galinė sienelė — priskaičiuoja
    // per daug").
    const realFurnitureGroups = groupCabinetsByFurniture(safeCabinets);
    const realTotalWidth = realFurnitureGroups.reduce((sum, g) => sum + g.width, 0) || generalWidth;
    const realBackWallArea = realFurnitureGroups.reduce((sum, g) => sum + (g.width * g.height), 0) || (generalWidth * generalHeight);

    // ClickUp "2. Furnitūra. Vidiniai komponentai lieka sistemos atmintyje, tai tuos
    // 'nematomus' komponentus taip pat sistema prideda" — vartotojas: yra DAUGIAU būdų
    // padaryti kabyklą/stalčių/lentyną "nematomą" 3D vaizde, ne tik ištrinant per dialogą
    // (pvz. tiesiog PADALIJUS tą skyrių į du, pridedant vidinę plokštę per "Vidus"
    // žingsnį) — ir bet kuriuo iš jų sectionOptions raktas gali likti nebeatitinkantis
    // jokio DABAR egzistuojančio lapo, o calculatePrice.js AKLAI skaičiuodavo VISUS
    // sectionOptions įrašus (Object.values(...).forEach), niekada nepatikrinęs, ar jų
    // raktas iš viso dar egzistuoja dabartiniame cab.structure/interiorStructure medyje.
    // Vietoj to, kad reikėtų lopyti KIEKVIENĄ galimą Layout.jsx mutacijos vietą (visada
    // rizikuojant praleisti dar vieną — kaip ir nutiko su Vidus skaidymu, žr.
    // handleInteriorHSplit/handleInteriorVSplit, kurie neiškviečia
    // remapSectionOptionsAfterStructureChange), kaina dabar VISADA tikrina TIESIOGIAI,
    // "gyvai", ar raktas atitinka dabartinį medžio lapą — nepriklausomai nuo to, kokia
    // mutacija (jei kokia nors) sectionOptions liko neišvalytas.
    const getLeafPaths = (node, path = '0') => {
        if (!node || !node.children) return [path];
        return node.children.flatMap((child, i) => getLeafPaths(child, `${path}-${i}`));
    };
    // Kiekvieno kabineto (LOKALUS indeksas šiame `cabinets` masyve) šiuo metu galiojančių
    // sectionOptions rakto GALŪNIŲ sąrašas — furnitureId prefikso čia tyčia neparsiname
    // (nežinome tikslaus jo turinio šioje f-joje), tik TIKRINAME, ar visas raktas
    // BAIGIASI atitinkama galūne (žr. isLiveSectionOptionKey žemiau) — kiekvienas
    // IŠORINIS lapas ("${idx}-${path}"), IR, jei tas išorinis lapas šiuo metu turi savo
    // interiorStructure poaugį (Vidus pertvara), kiekvienas TO poaugio VIDINIS lapas,
    // TIKSLIAI TUO PAČIU raktu, kokį naudoja SingleCabinet3D.jsx createInnerRenderFn
    // ("${idx}-INNER__${outerPath}__${innerPath}").
    const validSectionOptionSuffixes = safeCabinets.flatMap((cab, idx) => {
        const outerLeaves = getLeafPaths(cab.structure || {});
        const suffixes = outerLeaves.map(p => `${idx}-${p}`);
        outerLeaves.forEach(outerPath => {
            // NEBŪTINA tikrinti ar interiorStructure[outerPath] turi .split — kai sekcija
            // NETURI durelių (arba tiesiog šiuo metu žiūrima per Vidų), SingleCabinet3D.jsx
            // createInnerRenderFn naudoja "INNER__${outerPath}__0" raktą JAU nuo pat pradžių,
            // NET NEPADALIJUS į du (žr. RecursiveSection ankstyvą return, kai !hasDoorAssigned
            // arba activeStep==='Vidus' — tada renderinama per vidinį medį, kurio šaknis "0"
            // ir be split lieka vienas lapas). Anksčiau čia buvo tikrinama TIK kai split===true,
            // tad kiekviena be-durelių sekcija (labai dažnas atvejis — atviros lentynos) su
            // TIESIOG viena kabykla/lentyna (be vidinio skaidymo) buvo laikoma "negyvo" rakto ir
            // NEĮSKAIČIUOJAMA į kainą — realus per-mažas kainavimas, ne per-didelis. getLeafPaths
            // grąžina ['0'] kai nėra split, ir gilesnius lapus kai yra — todėl čia visada saugu
            // kviesti be sąlygos.
            const innerNode = (cab.interiorStructure || {})[outerPath] || {};
            getLeafPaths(innerNode).forEach(innerPath => {
                suffixes.push(`${idx}-INNER__${outerPath}__${innerPath}`);
            });
        });
        return suffixes;
    });
    const isLiveSectionOptionKey = (key) => validSectionOptionSuffixes.some(sfx => key.endsWith(`-${sfx}`));

    // --- 1. KARKASAS ---
    let carcassPanels = [];
    let areaLMDP = 0;
    let areaMDF = 0;
    // Ploto-svertinė (area-weighted) kiekvienos kategorijos SKYDŲ SAVOS kainos suma —
    // žr. priceLMDP/priceMDF žemiau dėl to, kodėl šitai reikalinga (anksčiau VISA
    // LMDP kategorija buvo kainuojama VIENA selectedPlate.price reikšme, o VISA MDF —
    // fiksuotu 45 €/m², visiškai ignoruojant kiekvienam konkrečiam šonui pasirinktą
    // specificPlate/leftPlate/rightPlate/topPlate/bottomPlate savą kainą — žr.
    // /admin/bugs "Pridedant ar keiciant plokstes, nepersiskaiciuoja kaina").
    let lmdpWeightedPriceSum = 0;
    let mdfWeightedPriceSum = 0;

    // specificPlate === undefined (4-as argumentas visai nepaduotas) — skydas
    // NĖRA susietas su konkrečiu šonu (pvz. lentyna), naudojame bendrą
    // numatytąją plokštę kaip anksčiau. specificPlate === null (aiškiai
    // paduota null reikšmė) — skambinta su AIŠKIAI dar nepasirinktu konkrečiu
    // šonu/durų grupe ("skirtingos plokštės" rėžimu tas šonas dar nepažymėtas)
    // — TOKIU ATVEJU skydo VISAI NEKAINUOJAME (nulinis plotas), o ne fantomine
    // numatytąja/STD plokšte (žr. ClickUp "LMD (skirtingos plokštės) -
    // priskaičiuoja, nors jie nėra pasirinkti").
    const addPanel = (w, h, name, specificPlate) => {
        if (specificPlate === null) return;
        const safeW = Number(w) || 0;
        const safeH = Number(h) || 0;
        const area = safeW * safeH;
        const plate = specificPlate || selectedPlate || { panelType: 0, price: 20, code: 'STD' };
        const isMDF = plate.panelType === 1;
        const platePrice = Number(plate.price) || 0;

        carcassPanels.push({
            name, width: safeW, height: safeH, area, material: isMDF ? 'MDF' : 'LMDP', code: plate.code, price: plate.price
        });

        if (isMDF) { areaMDF += area; mdfWeightedPriceSum += area * platePrice; }
        else { areaLMDP += area; lmdpWeightedPriceSum += area * platePrice; }
    };

    safeCabinets.forEach((cab, idx) => {
        const depth = Number(cab.depth) || 0.6;
        const width = Number(cab.width) || 0;
        const height = Number(cab.height) || 0;
        const cabBasisType = cab.basisType ?? basisType;

        addPanel(depth, height, `Spinta ${idx + 1} Kairė`, separatePlates ? leftPlate : undefined);
        addPanel(depth, height, `Spinta ${idx + 1} Dešinė`, separatePlates ? rightPlate : undefined);
        addPanel(width - (wallThickness * 2), depth, `Spinta ${idx + 1} Viršus`, separatePlates ? topPlate : undefined);
        // basisType 7/8 praleidžiama ČIA — tų dviejų tipų dugnas kainuojamas kaip
        // VIENA "Pagrindo plokštė" žemiau (1b skyriuje), tiksliai tokio paties
        // dydžio (UnifiedBase.jsx grupės lygio plokštė), kaip tikrame 3D modelyje.
        // Skaičiuojant ir "Apačia" ČIA, IR "Pagrindo plokštė" 1b skyriuje, TAS
        // PATS dugnas būdavo kainuojamas DU KARTUS (žr. ClickUp "1. Plokštės.
        // LMDP. Realus skaičius - priskaičiuoja per daug. Dėl Pagrindo").
        if (cabBasisType !== 0 && cabBasisType !== 7 && cabBasisType !== 8) {
            addPanel(width - (wallThickness * 2), depth, `Spinta ${idx + 1} Apačia`, separatePlates ? bottomPlate : undefined);
        }
        (cab.shelves || []).forEach((s, shelfIdx) => {
            addPanel(width - (wallThickness * 2), depth, `Spinta ${idx + 1} Lentyna ${shelfIdx + 1}`);
        });
        // Anksčiau `key.split('-').map(Number)[0]` — tai grąžindavo NaN, kai raktas
        // prasideda furnitureId (pvz. "FIT12345-0-1"), tad ši šaka NIEKADA nesuveikdavo
        // jokiam realiam projektui. Dabar tikrinama TIESIOGIAI, ar raktas baigiasi ŠIO
        // kabineto DABAR galiojančia išorinio lapo galūne (žr. validSectionOptionSuffixes
        // aukščiau) — kartu išsprendžia ir "gyvai" patikrą (žr. ten komentarą).
        const ownSuffixes = validSectionOptionSuffixes.filter(sfx => sfx.startsWith(`${idx}-`));
        Object.entries(sectionOptions).forEach(([key, val]) => {
            if (val !== 3) return; // 3 = Vertikalus
            if (!ownSuffixes.some(sfx => key.endsWith(`-${sfx}`))) return;
            const sectionH = 0.4; // Hardcoded nes neturime tikslaus section height čia
            addPanel(depth, sectionH, `Spinta ${idx + 1} Vertikalus`);
        });
    });

    // --- 1b. PAGRINDAS (Cokolis) — anksčiau skaičiuojamas TIK vaizdavimui,
    // niekada neįtraukiamas į kainą. Dabar kainuojamas kaip karkaso plokštė
    // (tos pačios plokštės/medžiagos kaina).
    // Anksčiau ČIA VISADA būdavo pridedamos VISOS 4 cokolio juostos (Priekis/
    // Galas/Kairė/Dešinė) bet kuriam basisType, kuris nebuvo 0 ar 5 — bet
    // realiame 3D modelyje (UnifiedBase.jsx) kiekvienas Pagrindo tipas piešia
    // VISIŠKAI SKIRTINGĄ plokščių rinkinį: Cokolis (2) apskritai neturi galinės
    // lentos (baldas atsuktas prie sienos, jos nesimato); Kairė/Dešinė iki
    // grindų (3/4) turi tik priekį + VIENĄ šoną (kitas šonas siekia grindis be
    // jokios lentos); Kojelės (6) neturi jokios plokštės — tik metalinės
    // kojelės; Plokštė (Tarp šonų / Per visą plotį) (7/8) yra VIENA plona
    // dugno plokštė, ne keturios 10cm juostos. Anksčiau visi šie tipai vis
    // tiek būdavo apkrauti tomis pačiomis 4 pilnomis juostomis, kas ir
    // sukeldavo per didelį LMDP kiekį (žr. ClickUp "1. Plokštės. LMDP...
    // priskaičiuoja per daug. Dėl Pagrindo"). Dabar kiekvienam tipui
    // skaičiuojamos TIK tos plokštės, kurios realiai egzistuoja 3D modelyje.
    //
    // ANTRAS radinys (ta pati ClickUp užduotis, vėliau pridėtas komentaras
    // "manau, kažkas papildomai prisideda... realiai arčiau 3,96m2 turėtų
    // būti"): basisType 2/3/4 atveju ta pusė, kuri NESIEKIA grindų
    // (leftFloorReach/rightFloorReach === false SingleCabinet3D.jsx), 3D
    // modelyje VIS TIEK susideda iš DVIEJŲ sudėtų lentų — trumpesnio tikro
    // šono (nuo pagrindo zonos viršaus iki viršaus) + siauros cokolio juostos
    // po juo (nuo grindų iki pagrindo zonos viršaus). Bendras TOS PUSĖS
    // medžiagos kiekis (abi lentos sudėjus) lygus TIKSLIAI tai, ką jau
    // suskaičiuoja aukščiau esantis karkaso "Kairė"/"Dešinė" addPanel(depth,
    // height, ...) — jis naudoja PILNĄ cab.height (grindys iki viršaus),
    // neatsižvelgdamas, ar tas šonas realiai nupieštas kaip VIENA, ar kaip
    // DVI sudėtos lentos. Pridėjus ČIA dar ir atskirą "Cokolis Kairė"/"Cokolis
    // Dešinė" juostą, ta pati apatinė dalis būdavo paskaičiuota DU KARTUS —
    // štai kodėl "Naujoje versijoje" (po pirmo šio bug'o fix'o) plotas net
    // PADIDĖJO, o ne sumažėjo. Priekinė juosta (Cokolis Priekis) ŠIO bug'o
    // neturi — ji yra VISIŠKAI PAPILDOMA medžiaga, kurios niekas kitas
    // nekainuoja (karkaso cikle "priekio" plokštės apskritai nėra, durys
    // kainuojamos atskirai), tad ji lieka.
    let basePanels = [];
    safeCabinets.forEach((cab, idx) => {
        const cabBasisType = cab.basisType ?? basisType;
        const width = Number(cab.width) || 0;
        const depth = Number(cab.depth) || 0.6;
        const hBase = 0.1; // 10cm
        const plate = separatePlates ? bottomPlate : undefined;
        const addBase = (name, w, h) => {
            basePanels.push({ name, width: w, height: h });
            addPanel(w, h, name, plate);
        };

        if (cabBasisType === 1) { // Padas — TIK priekinė cokolio juosta (UnifiedBase.jsx type 1
            // piešia vien priekinę juostą; šonai realiai siekia grindis kaip tikri šoniniai
            // skydai — jau įskaičiuoti aukščiau — o galo iš viso nėra, žr. ClickUp "1. Plokštės.
            // LMDP... priskaičiuoja per daug. Dėl Pagrindo").
            addBase(`Spinta ${idx + 1} Cokolis Priekis`, width, hBase);
        } else if (cabBasisType === 2) { // Cokolis — priekis, BE galo. Šonai NEPRIDEDAMI — jų
            // medžiaga jau visa įskaičiuota karkaso "Kairė"/"Dešinė" (pilnas cab.height), žr.
            // komentarą aukščiau.
            addBase(`Spinta ${idx + 1} Cokolis Priekis`, width, hBase);
        } else if (cabBasisType === 3 || cabBasisType === 4) { // Kairė/Dešinė iki grindų — TIK priekis,
            // ta pati "šonas jau įskaičiuotas karkase" priežastis kaip type 2 aukščiau.
            addBase(`Spinta ${idx + 1} Cokolis Priekis`, Math.max(0, width - wallThickness), hBase);
        } else if (cabBasisType === 7) { // Plokštė (Tarp šonų) — viena plona dugno plokštė
            addBase(`Spinta ${idx + 1} Pagrindo plokštė`, Math.max(0, width - wallThickness * 2), depth);
        } else if (cabBasisType === 8) { // Plokštė (Per visą plotį) — viena dugno plokštė per visą plotį
            addBase(`Spinta ${idx + 1} Pagrindo plokštė`, width, depth);
        }
        // 0 (Nepasirinkta), 5 (Be pagrindo), 6 (Kojelės) — jokios LMDP/MDF plokštės.
    });

    // --- 1c. Falšjuosta/falšfasadas/papildomas šonas ir dengiančios plokštės —
    // anksčiau visiškai neįskaičiuojamos į kainą, nors sunaudoja medžiagą.
    // Kainuojamos kaip papildomos plokštės, ta pačia karkaso plokštės kaina.
    const furnitureGroups = realFurnitureGroups;
    furnitureGroups.forEach((group) => {
        const first = group.cabinets[0];
        const leftW = sideElementWidthM(first?.leftSideElement);
        const rightW = sideElementWidthM(first?.rightSideElement);
        const plate = separatePlates ? leftPlate : undefined;
        if (leftW > 0) addPanel(leftW, group.height, `${group.label} Kairys šonas (falšas)`, plate);
        if (rightW > 0) addPanel(rightW, group.height, `${group.label} Dešinys šonas (falšas)`, separatePlates ? rightPlate : undefined);

        const cp = coverPlates?.[group.furnitureId];
        if (cp?.left?.enabled) addPanel(group.height, group.depth, `${group.label} Dengianti plokštė kairė`, separatePlates ? leftPlate : undefined);
        if (cp?.right?.enabled) addPanel(group.height, group.depth, `${group.label} Dengianti plokštė dešinė`, separatePlates ? rightPlate : undefined);
        if (cp?.top?.enabled) addPanel(group.width, group.depth, `${group.label} Dengianti plokštė viršus`, separatePlates ? topPlate : undefined);
        if (cp?.back?.enabled) addPanel(group.width, group.height, `${group.label} Dengianti plokštė galas`);
    });

    const billableLMDP = calculateBillableArea(areaLMDP);
    const billableMDF = calculateBillableArea(areaMDF);

    // Ploto-svertinis VIDURKIS iš realiai KIEKVIENAM skydui priskirtos plokštės kainos
    // (žr. lmdpWeightedPriceSum/mdfWeightedPriceSum aukščiau) — PAKEIČIA anksčiau čia
    // buvusią VIENĄ, visai kategorijai bendrą reikšmę (selectedPlate.price LMDP-ui,
    // fiksuotas 45 MDF-ui), kuri VISIŠKAI ignoruodavo separatePlates/leftPlate/
    // rightPlate/topPlate/bottomPlate pasirinkimus perskaičiuojant kainą (patvirtinta
    // gyvai: pakeitus vieno šono plokštę iš 67.68 į 14.9 €/m², finalPrice NEPASIKEISDAVO
    // — žr. /admin/bugs "Pridedant ar keiciant plokstes, nepersiskaiciuoja kaina").
    // areaLMDP/areaMDF === 0 atveju grįžtama prie senų atsarginių reikšmių, nes ten
    // dalyba iš nulio; tai neturi jokios įtakos totalCarcassPrice, kadangi
    // calculateBillableArea(0) === 0 bet kokiu atveju.
    const priceLMDP = areaLMDP > 0 ? (lmdpWeightedPriceSum / areaLMDP) : (selectedPlate?.price || 20);
    const priceMDF = areaMDF > 0 ? (mdfWeightedPriceSum / areaMDF) : 45;
    const totalCarcassPrice = (billableLMDP * priceLMDP) + (billableMDF * priceMDF);


    // --- 2. DURYS ---
    let doorPanels = [];
    let doorsAreaLMDP = 0;
    let doorsAreaMDF = 0;
    // Ploto-svertinė kiekvienos kategorijos durų plokščių SAVOS kainos suma — ta
    // pati priežastis/pataisymas, kaip lmdpWeightedPriceSum/mdfWeightedPriceSum
    // aukščiau karkasui: avgDoorPriceLMDP/avgDoorPriceMDF anksčiau paimdavo TIK
    // PIRMOS rastos durų grupės kainą (".find(...)?.price"), o ne realų vidurkį,
    // tad kitų durų grupių su KITOKIA plokšte/kaina pasirinkimas apskritai
    // neveikdavo totalDoorsPrice.
    let doorsLmdpWeightedPriceSum = 0;
    let doorsMdfWeightedPriceSum = 0;

    const doorGroups = {};
    mergedSections.forEach(sec => {
        if (!doorGroups[sec.groupId]) doorGroups[sec.groupId] = [];
        doorGroups[sec.groupId].push(sec);
    });
    // Solo (niekada nesujungtos) durys egzistuoja TIK kaip doorColors įrašas, be
    // jokio mergedSections įrašo (žr. Layout.jsx handleDoorSelection komentarą —
    // savireferentinis mergedSections įrašas kiekvienam durų priskyrimui buvo
    // sąmoningai pašalintas, nes lauždavo matmenų redagavimą). calculatePrice
    // anksčiau apie tokias duris VISIŠKAI nežinodavo (skaitė tik mergedSections),
    // tad jų MEDŽIAGA IR VYRIAI tyliai nebūdavo įskaičiuojami — pridedame
    // savireferentinę grupę kiekvienam doorColors raktui, kurio dar nėra tarp
    // mergedSections grupių (ta pati sintezė, kaip PlatesFillingsAndHandles.jsx
    // doorGroups useMemo).
    const mergedDoorKeys = new Set();
    mergedSections.forEach(sec => {
        mergedDoorKeys.add(`${sec.furnitureId}-${sec.cabinetIndex}-${sec.sectionIndex}`);
        mergedDoorKeys.add(`${sec.furnitureId}-${sec.targetCabinetIndex}-${sec.targetSectionIndex}`);
    });
    Object.entries(doorColors).forEach(([key, doorOption]) => {
        if (!doorOption || doorGroups[key] || mergedDoorKeys.has(key)) return;
        const dash1 = key.indexOf('-');
        const dash2 = key.indexOf('-', dash1 + 1);
        if (dash1 === -1 || dash2 === -1) return;
        const furnitureId = key.slice(0, dash1);
        const cabinetIndex = Number(key.slice(dash1 + 1, dash2));
        const sectionIndex = key.slice(dash2 + 1);
        doorGroups[key] = [{ furnitureId, cabinetIndex, sectionIndex, targetCabinetIndex: cabinetIndex, targetSectionIndex: sectionIndex }];
    });

    Object.entries(doorGroups).forEach(([groupId, sections]) => {
        const firstSec = sections[0];
        // cabinetIndex yra reliatyvus TIK TOS PAČIOS furnitureId kabinetams (žr.
        // groupCabinetsByFurniture.js komentarą — "mergedSections/sectionOptions/
        // doorHandles raktai referencuoja cabinetIndex TOS filtruotos tvarkos
        // atžvilgiu"), o NE globalus indeksas į VISĄ projekto cabinets masyvą.
        // Anksčiau čia buvo naudojamas tiesiai safeCabinets[firstSec.cabinetIndex]
        // — kelių baldų projekte (arba su pasenusiu mergedSections įrašu iš jau
        // IŠTRINTO baldo) tai priskirdavo durų matmenis/vyrius VISIŠKAI KITAM,
        // atsitiktiniam kabinetui, atsidūrusiam ta pačia pozicija globaliame
        // masyve — patvirtinta gyvai per window.__DEBUG_calc/__DEBUG_settings
        // (žr. ClickUp "2. Furnitūra. Lankstai..." klaidų seriją).
        const cabsForFurniture = safeCabinets.filter(c => c.furnitureId === firstSec.furnitureId);
        const cab = cabsForFurniture[firstSec.cabinetIndex];
        if (!cab) return;
        const cabWidth = Number(cab.width) || 0;
        const cabHeight = Number(cab.height) || 0;
        // Durų (ir per jas — vyrių kiekio, žr. sideHingeCount žemiau) aukštis anksčiau
        // buvo skaičiuojamas iš VISO kabineto aukščio (cabHeight - 2×wallThickness),
        // VISIŠKAI IGNORUODAMAS Pagrindo/cokolio zoną (visualBaseHeight) — jei baldas
        // turėjo, pvz., 10cm pagrindą, durys pagal ŠITĄ formulę "užauga" 10cm didesnės,
        // nei realiai piešiamos 3D/View2D.jsx brėžinyje (durys prasideda TIK virš
        // pagrindo zonos, žr. SingleCabinet3D.jsx carcassHeight = cabHeight -
        // visualBaseHeight). Per didelis apskaičiuotas durų aukštis stumdavo vyrių
        // kiekį į aukštesnę pakopą, nei durys realiai reikalauja (žr. ClickUp "2.
        // Furnitūra. Lankstai. Skaičiavimas nesilaiko taisyklės" — "dabar turim
        // konkretų durelių aukštį, tai manau galim tiesiog jį ir naudoti šioje
        // formulėje"). Pataisyta: ta pati visualBaseHeight formulė, kaip
        // SingleCabinet3D.jsx (0 tipui 0, 0.1 tipui 5, kitaip cab.basisHeight),
        // skaitoma iš PER KABINETĄ cab.basisType/cab.basisHeight (View2D.jsx
        // Cabinet2D taip pat naudoja cab.basisType/cab.basisHeight, ne bendrą
        // projekto reikšmę) — atimama iš innerHeight PRIEŠ perduodant
        // getSectionBounds, kad struktūros/durų ribos skaičiuotųsi TIK virš
        // pagrindo zonos, kaip ir tikrame 3D korpuse.
        const cabBasisType = Number(cab.basisType) || 0;
        const cabBasisHeight = Number(cab.basisHeight) || 0;
        // basisType 7/8 ("Plokštė Tarp šonų"/"Per Visą Plotį"): rezervuota tik plonos
        // plokštės storis (wallThickness), ne pilnas cabBasisHeight — durys ten prasideda
        // nuo plokštės viršaus, be tarpo, tad ir yra atitinkamai aukštesnės (žr.
        // SingleCabinet3D.jsx identišką visualBaseHeight komentarą — ta pati taisyklė
        // turi galioti ir čia, kad vyrių kiekis būtų skaičiuojamas nuo TO PATIES durų
        // aukščio, koks realiai piešiamas).
        // basisType 5 "Be pagrindo": 0.015 (ne 0.1) — suderinta su SingleCabinet3D.jsx/
        // View2D.jsx (ClickUp "2.0 Baldas. Pagrindas. Be pagrindo. Panaikinti apatinę
        // lentyną..." sumažino šio tipo tarpą nuo 10cm iki 1,5cm) — čia buvo likusi
        // sena reikšmė, tad vėl nebesutapo su realiu durų aukščiu (ta pati bendra
        // "Lankstai. Skaičiavimas nesilaiko taisyklės" problema).
        const cabVisualBaseHeight = cabBasisType === 0 ? 0 : (cabBasisType === 5 ? 0.015
          : ((cabBasisType === 7 || cabBasisType === 8) ? wallThickness : cabBasisHeight));
        const innerWidth = Math.max(0.001, cabWidth - wallThickness * 2);
        const innerHeight = Math.max(0.001, cabHeight - cabVisualBaseHeight - wallThickness * 2);

        const paths = new Set();
        sections.forEach(s => { paths.add(s.sectionIndex); paths.add(s.targetSectionIndex); });
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        paths.forEach(p => {
            if (p === undefined || p === null) return;
            const b = getSectionBounds(cab.structure, p, innerWidth, innerHeight, wallThickness);
            minX = Math.min(minX, b.x); minY = Math.min(minY, b.y);
            maxX = Math.max(maxX, b.x + b.width); maxY = Math.max(maxY, b.y + b.height);
        });
        const w = isFinite(minX) ? maxX - minX : innerWidth;
        const h = isFinite(minY) ? maxY - minY : innerHeight;

        // Durų sparno tipas (1=kairė, 2=dešinė, 3=dvigubos, 4/5=atverčiamos
        // viršus/apačia, 6=stalčius-vaizdinis) — iš doorColors, PAGAL PIRMOS
        // sekcijos raktą (grupės "pirminis" durų tipas, ta pati konvencija, kaip
        // PlatesFillingsAndHandles.jsx/enumeratePanels.js). Naudojama žemiau
        // sideHingeCount, kad vyriai būtų skaičiuojami TIK realioms varstomoms
        // durims (ne atverčiamoms/stalčiams) ir dvigubai dvigubų durų atveju.
        const doorOption = doorColors[`${firstSec.furnitureId}-${firstSec.cabinetIndex}-${firstSec.sectionIndex}`] || 0;
        const type = doorTypes[groupId] || 'standard';
        const baseHandleKey = `${firstSec.cabinetIndex}-${firstSec.sectionIndex}`;

        // Dvigubos durys (doorOption===3) — DVI fiziškai atskiros durelės, kiekviena
        // GALI turėti savo plokštę/filingą/veidrodį/rankenėlę (žr. ClickUp "3.0 Išorė.
        // Meniu - Dviguboms durims leidžiame pasirinkti konkrečias plokštes... T.y.
        // kairinėms ir dešininėms skirtingi pasirinkimai", ir to paties sprendimo
        // enumeratePanels.js atitikmenį) — tad kainuojame/eiluojame kaip DVI atskiras
        // puslapio ploto durų panel'es, ne vieną visai grupei. PlatesFillingsAndHandles.jsx
        // rašo doorPlates/doorMirrors/doorFillings[`${groupId}::L`/`::R`]; nesuffixuotas
        // doorPlates[groupId] paliktas kaip fallback senoms (prieš šį pakeitimą
        // sukurtoms) grupėms, kurios dar neturi atskiro kairės/dešinės pasirinkimo.
        const pushDoorPanel = (panelGroupId, panelW, panelH, selKey, handleKey, label) => {
            // Durų grupei dar nepasirinkus plokštės (doorPlates[selKey] tuščias),
            // NEKAINUOJAME fantomine STD plokšte — durų TIPAS/rankena/mechanizmai
            // (žemiau naudojantys doorPanels) tebeskaičiuojami kaip visada, tik
            // pati plokštės medžiaga (LMDP/MDF plotas) neįtraukiama, kol vartotojas
            // realiai jos nepasirenka (žr. ClickUp "LMD (skirtingos plokštės) -
            // priskaičiuoja, nors jie nėra pasirinkti" — "dar liko ... durys").
            const chosenDoorPlate = doorPlates[selKey] || doorPlates[groupId] || null;
            const plate = chosenDoorPlate || { panelType: 0, price: 20, code: 'STD' };
            const isMDF = plate.panelType === 1;
            const panelArea = panelW * panelH;
            const handle = doorHandles[handleKey];
            const filling = doorFillings[selKey] || doorFillings[groupId];
            const mirror = doorMirrors[selKey] || doorMirrors[groupId];

            doorPanels.push({
                name: label, groupId: panelGroupId, width: panelW, height: panelH, area: panelArea,
                material: isMDF ? 'MDF' : 'LMDP', code: plate.code, plate,
                type, handle, filling, mirror, price: plate.price, doorOption
            });

            if (chosenDoorPlate) {
                const doorPlatePrice = Number(plate.price) || 0;
                if (isMDF) { doorsAreaMDF += panelArea; doorsMdfWeightedPriceSum += panelArea * doorPlatePrice; }
                else { doorsAreaLMDP += panelArea; doorsLmdpWeightedPriceSum += panelArea * doorPlatePrice; }
            }
        };

        if (doorOption === 3) {
            pushDoorPanel(`${groupId}::L`, w / 2, h, `${groupId}::L`, `${baseHandleKey}::L`, `Grupė ${groupId} — Kairinės`);
            pushDoorPanel(`${groupId}::R`, w / 2, h, `${groupId}::R`, `${baseHandleKey}::R`, `Grupė ${groupId} — Dešininės`);
        } else {
            pushDoorPanel(groupId, w, h, groupId, baseHandleKey, `Grupė ${groupId}`);
        }
    });

    const billableDoorsLMDP = calculateBillableArea(doorsAreaLMDP);
    const billableDoorsMDF = calculateBillableArea(doorsAreaMDF);

    // Ploto-svertinis vidurkis (žr. priceLMDP/priceMDF komentarą aukščiau — ta pati
    // priežastis) — PAKEIČIA anksčiau čia buvusį ".find(...)?.price", kuris paimdavo
    // TIK PIRMOS durų grupės kainą visoms tos kategorijos durims.
    const avgDoorPriceLMDP = doorsAreaLMDP > 0 ? (doorsLmdpWeightedPriceSum / doorsAreaLMDP) : 20;
    const avgDoorPriceMDF = doorsAreaMDF > 0 ? (doorsMdfWeightedPriceSum / doorsAreaMDF) : 45;

    const totalDoorsPrice = (billableDoorsLMDP * avgDoorPriceLMDP) + (billableDoorsMDF * avgDoorPriceMDF);


    // --- 3. BRIAUNOS IR KLIJAI ---
    // cab.depth/width jau yra METRAIS (žr. DEFAULT_FURNITURE_TEMPLATE Layout.jsx) —
    // anksčiau PriceDialog.jsx čia dar kartą dalindavo iš 100, nuvertindamas
    // briaunų/klijų kainą ~100x. Ilgis skaičiuojamas metrais, be perteklinės /100.
    //
    // Briaunos kaina anksčiau VISADA naudojo TIK selectedPlate.edge_price_thin,
    // net kai separatePlates įjungta ir kiekvienas šonas turi SAVO plokštę (tą
    // pačią klaidos šeimą, kaip karkaso/durų kaina aukščiau — žr. /admin/bugs
    // "Pridedant ar keiciant plokstes, nepersiskaiciuoja kaina"). Dabar d*2+w*2
    // (kairė+dešinė+viršus+apačia) SKAIDOMA į 4 atskirus ilgius, kiekvienas
    // priskiriamas TAM ŠONUI, kurio front kraštą jis atitinka — TA PATI
    // depth/width→šonas logika, kaip addPanel kvietimai žemiau (Kairė/Dešinė =
    // depth, Viršus/Apačia = width). Kai separatePlates=false (undefined
    // paduodamas visoms 4 pusėms), addEdge visada krenta į selectedPlate —
    // bitiškai TAS PATS ilgis/kaina, kaip anksčiau. Lentynos VISADA naudoja
    // selectedPlate (specificPlate niekada nepaduodamas), lygiai kaip jų PAČIŲ
    // addPanel kvietimas aukščiau.
    let edgeM = 0;
    let edgeWeightedPriceSum = 0;
    const edgePriceFor = (specificPlate) => {
        if (specificPlate === null) return null; // šonas dar nepasirinktas — nekainuojame, kaip ir pats skydas
        const plate = specificPlate || selectedPlate;
        return Number(plate?.edge_price_thin) || 2.5;
    };
    const addEdge = (length, specificPlate) => {
        const price = edgePriceFor(specificPlate);
        if (price === null) return;
        edgeM += length;
        edgeWeightedPriceSum += length * price;
    };
    safeCabinets.forEach(cab => {
        const d = Number(cab.depth) || 0.6;
        const w = Number(cab.width) || 0;
        addEdge(d, separatePlates ? leftPlate : undefined);
        addEdge(d, separatePlates ? rightPlate : undefined);
        addEdge(w, separatePlates ? topPlate : undefined);
        addEdge(w, separatePlates ? bottomPlate : undefined);
        (cab.shelves || []).forEach(() => { addEdge(w * 2, undefined); });
    });
    const edgePriceThin = edgeM > 0 ? (edgeWeightedPriceSum / edgeM) : (Number(selectedPlate?.edge_price_thin) || 2.5);
    const edgePriceThick = edgePriceThin * 1.69;
    const edgeThinTotal = edgeM * edgePriceThin;
    const edgeThickTotal = edgeM * edgePriceThick;
    const glueTotal = edgeM * 1.69;


    // --- 4. PAGRINDAS - KOJELĖS ---
    // Kojelių kaina taikoma TIK kai pagrindo tipas realiai yra "Kojelės" (6) —
    // anksčiau čia buvo taikoma visiems pagrindo tipams nepriklausomai nuo to,
    // ar baldas iš tikrųjų turi kojeles.
    const legCount = getLegCount(realTotalWidth);
    const legPrice = 5;
    const legsTotal = basisType === 6 ? legCount * legPrice : 0;


    // --- 5. MECHANIZMAI IR PRIEDAI ---

    let drawerCount = 0;
    let hangerCount = 0;
    let pullOutCount = 0;
    let pantographCount = 0;
    let adjustCount = 0;
    // Tipas 11 "Lentyna su bėgeliais" (InnerSelectingDialog.jsx) anksčiau visai
    // nebuvo skaičiuojamas čia (nebuvo t===11 šakos), tad PriceDialog.jsx eilutė
    // klaidingai rodydavo pullOutCount (tipo 8 "Ištraukiama kabykla" skaitiklį),
    // todėl pridėjus TIK tipą 11 komponentą suma likdavo 0.00 € (žr. ClickUp
    // "2. Furnitūra. Nepridėjo nei vieno vidinio komponento."). Atskiras skaitiklis.
    let railShelfCount = 0;
    // "Gyvai" patikra (žr. isLiveSectionOptionKey/validSectionOptionSuffixes aukščiau) —
    // praleidžiama BET KURIO rakto, kuris NEBEATITINKA dabartinio structure/
    // interiorStructure medžio lapo, nepriklausomai nuo to, KOKIA mutacija (jei kokia
    // nors) jį paliko — vartotojas: "visda turi tikrinti gyvai ar vidiniai komponentai
    // egzistuoja 3D vaizde ar ne, jeigu taip, tada itraukiami i kaina, jeigu ne, tada ju
    // ir neturi būti".
    Object.entries(sectionOptions).forEach(([key, opt]) => {
        if (!isLiveSectionOptionKey(key)) return;
        flattenSectionTypes(opt).forEach(t => {
            if ([4, 5, 6].includes(t)) drawerCount += 1;
            if (t === 7) drawerCount += 2;
            if ([1, 2].includes(t)) hangerCount += 1;
            if (t === 8) pullOutCount += 1;
            if (t === 9) pantographCount += 1;
            if (t === 10) adjustCount += 1;
            if (t === 11) railShelfCount += 1;
        });
    });
    doorPanels.forEach(d => {
        if (d.type === 'drawer') drawerCount += 1;
    });

    let drawerMultiplier = quality === 2 ? 26 : (quality === 1 ? 11 : 4);
    let drawerTotal = drawerCount * drawerMultiplier;
    const maxDepth = safeCabinets.length ? Math.max(...safeCabinets.map(c => Number(c.depth) || 0)) : 0;
    if (maxDepth > 0.6) drawerTotal *= 2;


    let downDoorCount = doorPanels.filter(d => d.type === 'down').length;
    let downMultiplier = quality === 2 ? 33.11 : (quality === 1 ? 8.42 : 3.01);
    let downTotal = downDoorCount * downMultiplier;
    if (maxDepth > 0.65) downTotal *= 1.5;


    let upDoorCount = doorPanels.filter(d => d.type === 'up').length;
    let upMultiplier = quality === 2 ? 23.38 : 3.02;
    let upTotal = upDoorCount * upMultiplier;
    if (maxDepth > 0.6) upTotal *= 2;

    let hangerMultiplier = quality === 2 ? 15.92 : 10;
    let hangerTotal = hangerCount * hangerMultiplier;
    let pullOutTotal = pullOutCount * 9.98;
    let pantographTotal = pantographCount * 39.13;
    let adjustTotal = adjustCount * 5;
    // Kainuojama kaip stalčiaus bėgeliai (tas pats multiplier pagal kokybę) —
    // ištraukiama lentyna naudoja tuos pačius bėgelius kaip stalčius.
    let railShelfTotal = railShelfCount * drawerMultiplier;

    // Šoniniai lankstai — pagal durų aukštį (kiek lankstų vienoms durims reikia).
    // Anksčiau ČIA buvo SAVA riba (<0.8→2, <1.7→3, else→4), NESUTAMPANTI su
    // View2D.jsx techninio brėžinio (ir dabar — gamybinio brėžinio) vyrių
    // skaičiumi (hingeCountForHeight: <0.90→2, <1.60→3, <2.20→4, else→5) —
    // aukštoms (≥1.6m) durims kainos suvestinė ir brėžinys rodydavo SKIRTINGĄ
    // vyrių kiekį tai pačiai durelei. Dabar abu naudoja TĄ PAČIĄ funkciją.
    // doorOption (žr. doorPanels.push aukščiau): 1/2 = viena varstoma durelė (1
    // lapas), 3 = dvigubos durys, 4/5 = atverčiamos viršus/apačia (be šoninių
    // vyrių šiame modelyje), 6 = stalčius/vaizdinis kodas (irgi be vyrių).
    // Anksčiau ČIA vyriai buvo pridedami PER KIEKVIENĄ doorPanels įrašą BE JOKIO
    // doorOption patikrinimo — tad atverčiamos/stalčiaus "durys" irgi gaudavo
    // vyrių kainą, o dvigubos durys (2 lapai) buvo skaičiuojamos kaip VIENOS
    // (žr. ClickUp "2. Furnitūra. Lankstai." klaidų seriją — "visada prideda
    // dvigubai daugiau..."), tad doorOption===3 atveju "n * 2" KOMPENSUODAVO
    // vieną bendrą (visai grupei) doorPanels įrašą. Nuo kai doorPanels.push
    // aukščiau DVIGUBOMS durims pradėjo generuoti DU ATSKIRUS įrašus (po vieną
    // kiekvienam fiziniam lapui — žr. ClickUp "Dviguboms durims leidžiame
    // pasirinkti konkrečias plokštes..."), "2 lapai" dabar jau atspindėta PAČIU
    // doorPanels ilgiu, tad kiekvienas (bet kurio doorOption 1/2/3) įrašas prideda
    // TIK "n" — "n * 2" dviguboms liktų DVIGUBAI per daug (n už kiekvieną iš 2 jau
    // atskirų įrašų = n+n, ne (n*2)+(n*2)).
    let sideHingeCount = 0;
    doorPanels.forEach(d => {
        const n = hingeCountForHeight(d.height);
        if (d.doorOption === 1 || d.doorOption === 2 || d.doorOption === 3) sideHingeCount += n;
        // 4/5/6 — be šoninių vyrių.
    });
    const sideHingeMultiplier = quality === 2 ? 3.86 : (quality === 1 ? 2.45 : 0.8);
    const sideHingeTotal = sideHingeCount * sideHingeMultiplier;

    // Rankenėlės — sumuojama iš kiekvienos pasirinktos rankenėlės kainos.
    let handlesTotal = 0;
    Object.values(doorHandles).forEach(h => { if (h && h.price) handlesTotal += Number(h.price); });


    // --- 6. KITI MOKESČIAI ---

    let otherTotal = realTotalWidth * 18.15;
    let designTotal = realTotalWidth * 16.5;

    let backWallTotal = 0;
    let backWallCount = 0;
    const backWallArea = realBackWallArea;
    if (backWall) {
        backWallCount = Math.ceil(backWallArea / (2.85 * 2.07));
        backWallTotal = backWallCount * 22.69;
    }

    // "Grindjuostės išpjovimas" kainos eilutė anksčiau buvo susieta su
    // skirtingBoardCutting — tai VISAI KITAS, patalpos lygio laukas (Patalpa
    // žingsnio "Grindjuostės" pasirinkimas: ar patalpoje apskritai yra
    // grindjuostė). Pati kainos eilutė ir vartotojo matomas perjungiklis
    // ("Grindjuostės išpjovimas: Taip/Ne", Baldas žingsnis, PER BALDĄ) yra
    // skirtingCutout — jis anksčiau apskritai nebuvo naudojamas kainos
    // skaičiavime, tad "Ne" pasirinkimas niekada nesumažindavo kainos (žr.
    // ClickUp "1. Plokštės. Grindjuostės išpjovimas visada įtrauktas į kainą,
    // nesvarbu, kad pasirinktas 'Ne'" — vartotojas patvirtino, kad tai vis dar
    // neveikia). skirtingCutout gali būti nustatytas PER kabinetą (kaip
    // basisType aukščiau) su furnitureData lygio atsargine reikšme; kainuojama
    // fiksuotu 20€ mokesčiu, jei BENT VIENAS kabinetas jį turi įjungtą.
    const anyCabinetNeedsSkirtingCutout = safeCabinets.some(cab => (cab.skirtingCutout ?? skirtingCutout) === true);
    let skirtingPrice = anyCabinetNeedsSkirtingCutout ? 20 : 0;

    // Išsimatavimas į namus — priklauso nuo pasirinktos paslaugos (professionalServices:
    // 0=Pasidaryk pats, 1=Pasidaryk su pagalba, 2=Meistras į namus), nuo įmontavimo tipo
    // (fittingType: 1="Be laisvumo tarpelių", kitaip "Su laisvumo tarpeliais") IR nuo baldo
    // tipo (type/furnitureType: 1="Spinta su stumdomis durimis") — žr. ClickUp "3.
    // Matavimas-projektavimas. Taisyklė", pažymėta "failed qa" su pastaba "nesuveikė vienas
    // IF'as": Su tarpeliais 0/28.57/57.14€ pagal paslaugą; Be tarpelių siūloma tik su
    // "Meistras į namus" — 128.57€ PAPRASTAI, bet TIK 71.43€ kai baldas yra stumdomų durų
    // spinta (furnitureType===1). Ankstesnė šios funkcijos versija (prieš šį fix'ą) šios
    // baldo-tipo sąlygos VISAI netikrino — visiems baldams be tarpelių+meistras grąžindavo
    // 128.57€, o stumdomų durų spintoms tai buvo per brangu (turėjo būti 71.43€). Kitoms 2
    // paslaugoms "Be tarpelių" pagal pateiktą lentelę yra n/a (neturėtų pasitaikyti), tad tuo
    // atveju grąžinama prie tos pačios paslaugos "Su tarpeliais" kainos, o ne 0€, kad duomenų
    // nenuoseklumo atveju nebūtų dosniau nei numatyta.
    const MEASUREMENT_SU_TARPELIAIS = [0, 28.57, 57.14];
    const svcIdx = [0, 1, 2].includes(professionalServices) ? professionalServices : 0;
    const isSlidingDoorWardrobe = parseInt(furnitureType, 10) === 1;
    const measurementPrice = (fittingType === 1)
        ? (svcIdx === 2 ? (isSlidingDoorWardrobe ? 71.43 : 128.57) : MEASUREMENT_SU_TARPELIAIS[svcIdx])
        : MEASUREMENT_SU_TARPELIAIS[svcIdx];

    const transportPrice = 58.47;


    // --- GALUTINĖ SAVIKAINA (BE projektavimo/administravimo antkainio) ---
    const grandTotal =
        totalCarcassPrice +
        totalDoorsPrice +
        edgeThinTotal +
        edgeThickTotal +
        glueTotal +
        legsTotal +
        drawerTotal +
        downTotal +
        upTotal +
        hangerTotal +
        pullOutTotal +
        pantographTotal +
        adjustTotal +
        railShelfTotal +
        sideHingeTotal +
        handlesTotal +
        otherTotal +
        designTotal +
        backWallTotal +
        skirtingPrice +
        measurementPrice +
        transportPrice;

    // --- PROJEKTAVIMO/ADMINISTRAVIMO ANTKAINIS — pridedamas TIK virš savikainos,
    // klientui rodomai/krepšelio kainai (finalPrice), NE į Suvestinės/PDF savikainą
    // (grandTotal).
    const projectionPct = professionalServices === 1 ? 0.2 : 0.15;
    const adminPct = professionalServices === 1 ? 0.25 : 0.30;
    const projectionTotal = grandTotal * projectionPct;
    const adminTotal = grandTotal * adminPct;

    // Adminas gali prie konkretaus baldo rankiniu būdu pridėti apmokestinamas
    // eilutes (pvz. papildomi darbai) — žr. "Paslaugos" žingsnį (admin-only).
    // Šios sumuojasi TIESIOGIAI į finalPrice (klientui rodomą/krepšelio kainą),
    // nes tai realus papildomas darbas, ne informacinė pastaba.
    const adminDescriptionTotal = (Array.isArray(adminDescriptionItems) ? adminDescriptionItems : [])
        .reduce((sum, it) => sum + (Number(it?.price) || 0), 0);

    const finalPrice = grandTotal + projectionTotal + adminTotal + adminDescriptionTotal;

    // --- PASIRENKAMOS PASLAUGOS (neįskaičiuota nei į grandTotal, nei į finalPrice) ---
    const assemblyM2 = billableLMDP + billableMDF + billableDoorsLMDP + billableDoorsMDF;
    const calcAssemblyPrice = Math.max(3, assemblyM2) * 10.89 + 30;
    const calcCarryPrice = assemblyM2 * 2.20 + 10;
    const calcDeliveryPrice = 45;
    const assemblyPrice = calcAssemblyPrice;
    const carryPrice = calcCarryPrice;
    const deliveryPrice = calcDeliveryPrice;

    return {
        carcassPanels,
        doorPanels,
        basePanels,
        internalStats: { drawerCount, hangerCount, pullOutCount, pantographCount, downDoorCount, upDoorCount, adjustCount, sideHingeCount, railShelfCount },

        areaLMDP, areaMDF, billableLMDP, billableMDF, priceLMDP, priceMDF, totalCarcassPrice,
        doorsAreaLMDP, doorsAreaMDF, billableDoorsLMDP, billableDoorsMDF, avgDoorPriceLMDP, avgDoorPriceMDF, totalDoorsPrice,

        edgeM, edgePriceThin, edgePriceThick, edgeThinTotal, edgeThickTotal, glueTotal,

        drawerTotal, drawerMultiplier,
        downTotal, downMultiplier,
        upTotal, upMultiplier,
        hangerTotal, hangerMultiplier,
        pullOutTotal,
        pantographTotal,
        adjustTotal,
        railShelfTotal,
        sideHingeTotal, sideHingeMultiplier,
        handlesTotal,

        otherTotal,
        designTotal,
        backWallTotal, backWallArea, backWallCount,
        skirtingPrice,
        legsTotal, legCount, legPrice,
        measurementPrice,
        transportPrice,

        grandTotal,
        projectionPct, adminPct, projectionTotal, adminTotal, finalPrice,

        assemblyM2, assemblyPrice, carryPrice, deliveryPrice,
        calcAssemblyPrice, calcCarryPrice, calcDeliveryPrice,
        adminDescriptionItems, adminDescriptionTotal,

        maxDepth
    };
};
